import { useEffect, useRef, useState, type PointerEvent } from "react";
import {
  DEMO_ANALYSES,
  DEMO_TOKENS,
  type DemoPanel,
  type Verdict,
} from "./demo";
import { DevFeesModal } from "./components/DevFeesModal";
import { InstallButton } from "./components/InstallButton";
import { LoyaltyHolderRewards } from "./components/LoyaltyHolderRewards";
import { Mascot, type MascotMood } from "./components/Mascot";
import { PRIVACY_POLICY_URL } from "./config";

const checks = [
  "Mint Authority",
  "Freeze Authority",
  "Holder Concentration",
  "Liquidity",
  "Token Age",
  "Insider Signals",
  "Creator History",
  "Bundled Wallets",
];

function Hero() {
  const [hovering, setHovering] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  const parallax = (event: PointerEvent<HTMLDivElement>) => {
    const box = stage.current?.getBoundingClientRect();
    if (!box || !stage.current) return;
    stage.current.style.setProperty(
      "--px",
      String((event.clientX - box.left) / box.width - 0.5),
    );
    stage.current.style.setProperty(
      "--py",
      String((event.clientY - box.top) / box.height - 0.5),
    );
  };
  return (
    <section className="hero shell" id="home">
      <div className="hero__copy">
        <span className="hero__gmgn-badge">GMGN ONLY ↗</span>
        <span className="eyebrow">Free Solana research extension</span>
        <h1 aria-label="TOKIN — YOUR MEMECOIN RESEARCH COMPANION.">
          <strong>TOKIN —</strong>
          <br />
          YOUR MEMECOIN
          <br />
          <em>RESEARCH COMPANION.</em>
        </h1>
        <p>
          Analyze Solana memecoins, explore on-chain data, and track risks —
          directly inside GMGN.
        </p>
        <div className="button-row">
          <div className="hero__install">
            <InstallButton />
            <div className="hero__install-copy">
              <strong>TOKIN is an overlay built exclusively for GMGN.</strong>
              <span>Open a Solana token on GMGN to use TOKIN.</span>
              <small>Independent extension. Not affiliated with GMGN.</small>
            </div>
          </div>
          <a className="button button--secondary" href="#demo">
            Explore the Demo ↓
          </a>
        </div>
        <div className="trust-row">
          <span>Free MVP</span>
          <span>No wallet required</span>
          <span>No auto-trading</span>
        </div>
      </div>
      <div
        ref={stage}
        className="hero-stage"
        onPointerMove={parallax}
        onPointerEnter={() => setHovering(true)}
        onPointerLeave={() => setHovering(false)}
      >
        <i className="pearl pearl--one" />
        <i className="pearl pearl--two" />
        <i className="hero-stage__ring" />
        <div className="overlay-preview glass">
          <div className="overlay-preview__top">
            <strong>TOKIN</strong>
            <span className="live">● Live</span>
          </div>
          <div className="token-line">
            <span className="token-orb">W</span>
            <span>
              <strong>WIF</strong>
              <small>Solana · Demo</small>
            </span>
            <b>$0.4372</b>
          </div>
          <div className="mini-metrics">
            <span>
              Risk<b>28</b>
            </span>
            <span>
              Verdict<b>Watch</b>
            </span>
            <span>
              Confidence<b>72%</b>
            </span>
          </div>
          <div className="preview-analysis">
            <i>◉</i>
            <span>
              <b>A clearer picture.</b>
              <small>Evidence-backed checks, directly on GMGN.</small>
            </span>
          </div>
        </div>
        <div className="hero-mascot">
          <Mascot
            mood={hovering ? "walk" : "idle"}
            size="large"
            label="Friendly glass research companion"
          />
          <span className="speech">Let’s explore this token! ✦</span>
        </div>
      </div>
      <div className="community-strip">
        <span className="live">● Coming soon</span>
        <b>Dev Fees → Loyalty Holders</b>
        <span>Terms and eligibility are not finalized.</span>
      </div>
    </section>
  );
}

function Demo() {
  const [panel, setPanel] = useState<DemoPanel>("analysis");
  const [verdict, setVerdict] = useState<Verdict>("WATCH");
  const [scanning, setScanning] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const select = (next: DemoPanel) => {
    setPanel(next);
    if (next === "analysis") {
      setScanning(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setScanning(false), 720);
    }
  };
  const analysis = DEMO_ANALYSES[verdict];
  const mood: MascotMood = scanning
    ? "scan"
    : panel === "analysis"
      ? (verdict.toLowerCase() as MascotMood)
      : "idle";
  return (
    <section className="demo shell" id="demo">
      <div className="section-heading">
        <span className="eyebrow">Interactive product demo</span>
        <h2>
          Try the overlay.
          <br />
          <em>No tab required.</em>
        </h2>
        <p>
          This simulation stays in your browser. It uses labeled sample data and
          makes no backend requests.
        </p>
      </div>
      <div className="demo-workbench glass">
        <aside className="demo-nav" aria-label="Demo tools">
          {(
            [
              ["analysis", "Full Token Analysis", "Structured risk assessment"],
              ["x", "X Search", "Find public conversations"],
              ["solscan", "Solscan Explorer", "Review on-chain evidence"],
              ["tracked", "My Tracked Tokens", "Monitor selected tokens"],
            ] as const
          ).map(([id, title, sub], i) => (
            <button
              key={id}
              onClick={() => select(id)}
              className={panel === id ? "active" : ""}
            >
              <span>0{i + 1}</span>
              <b>{title}</b>
              <small>{sub}</small>
            </button>
          ))}
        </aside>
        <div className="demo-screen">
          <div className="demo-screen__bar">
            <span className="demo-label">Demo data</span>
            <span>WIF · Solana</span>
            <span>•••</span>
          </div>
          <div className="demo-screen__body" aria-live="polite">
            {panel === "analysis" && (
              <div className="analysis-demo">
                <div className="verdict-tabs">
                  {(["BUY", "WATCH", "AVOID"] as const).map((item) => (
                    <button
                      key={item}
                      onClick={() => setVerdict(item)}
                      className={
                        verdict === item ? `active ${item.toLowerCase()}` : ""
                      }
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <div className="analysis-result">
                  <div className={`score-orb ${verdict.toLowerCase()}`}>
                    <strong>{scanning ? "…" : analysis.score}</strong>
                    <small>Risk Score</small>
                  </div>
                  <div>
                    <span className={`verdict ${verdict.toLowerCase()}`}>
                      {scanning ? "SCANNING" : analysis.verdict}
                    </span>
                    <h3>
                      {scanning ? "Reviewing demo signals…" : analysis.summary}
                    </h3>
                    <p>
                      Confidence {analysis.confidence}% · sample completeness
                      89%
                    </p>
                  </div>
                </div>
                <ul className="signal-list">
                  {analysis.checks.map((item) => (
                    <li key={item}>✓ {item}</li>
                  ))}
                </ul>
                <p className="fine-print">
                  BUY means the configured demo filters pass. It is not a safety
                  promise or recommendation.
                </p>
              </div>
            )}
            {panel === "x" && (
              <div className="tool-demo">
                <div className="tool-demo__icon">𝕏</div>
                <span className="eyebrow">Follow the conversation</span>
                <h3>Open a public X search for the token address.</h3>
                <p>
                  The real extension builds a search query. It does not invent
                  posts or create a fictional social score.
                </p>
                <button disabled className="button button--secondary">
                  Demo action only
                </button>
              </div>
            )}
            {panel === "solscan" && (
              <div className="solscan-demo">
                <span className="eyebrow">On-chain evidence · Demo data</span>
                <h3>Look beyond the chart.</h3>
                {[
                  ["Contract Address", "DemoCA…8xQ"],
                  ["Holder Concentration", "22.4%"],
                  ["Mint Authority", "Disabled"],
                  ["Freeze Authority", "Disabled"],
                  ["Creator Wallet", "DemoCreator…4pL"],
                ].map(([k, v]) => (
                  <div className="data-row" key={k}>
                    <span>{k}</span>
                    <b>{v}</b>
                  </div>
                ))}
                <p className="fine-print">
                  Illustrative fields only. Fake addresses are deliberately not
                  linked.
                </p>
              </div>
            )}
            {panel === "tracked" && (
              <div className="tracked-demo">
                <span className="eyebrow">
                  Active overlay monitoring · Demo data
                </span>
                <h3>Keep an eye on what matters.</h3>
                {DEMO_TOKENS.map((t) => (
                  <div className="tracked-row" key={t.symbol}>
                    <span className="token-orb">{t.symbol[0]}</span>
                    <b>{t.symbol}</b>
                    <span>Risk {t.score}</span>
                    <small>{t.alert}</small>
                  </div>
                ))}
                <p className="fine-print">
                  Free monitoring runs only while GMGN and the overlay are
                  active.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="demo-companion">
          <Mascot mood={mood} />
          <span>
            {scanning
              ? "Checking signals…"
              : panel === "analysis"
                ? `${verdict} reaction`
                : "Right beside you"}
          </span>
        </div>
      </div>
    </section>
  );
}

const faq = [
  [
    "Is the extension free?",
    "Yes. The current research, risk scan, X search, Solscan shortcut, tracking, and alerts experience is a free MVP.",
  ],
  [
    "Does it require a wallet connection?",
    "No. It does not request wallet access, private keys, seed phrases, or signatures.",
  ],
  [
    "Does it execute trades?",
    "No. The current product does not execute or automate trades.",
  ],
  [
    "What does BUY / WATCH / AVOID mean?",
    "They summarize configured risk filters. BUY is not financial advice, a safety certification, or a profit guarantee.",
  ],
  [
    "Does X Search analyze tweets?",
    "No. It opens a public X search for the token contract address.",
  ],
  [
    "How does token tracking work?",
    "Tokens are stored locally. Monitoring runs while GMGN and the overlay are active, subject to quotas and data availability.",
  ],
  [
    "What is Premium Mascot?",
    "A planned $99/month AI research companion. Its described abilities are not available yet.",
  ],
  [
    "What are Loyalty Holder Rewards?",
    "A planned program. Terms are not finalized and rewards are not guaranteed.",
  ],
  [
    "How do I install the extension?",
    "Through its official Chrome Web Store listing once that listing is configured.",
  ],
] as const;

export default function App() {
  const [modal, setModal] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setModal(true), 2000);
    return () => window.clearTimeout(id);
  }, []);
  return (
    <>
      <header className="site-header">
        <a className="brand" href="#home">
          <span>
            TOKIN<small>Memecoin research companion</small>
          </span>
        </a>
        <nav>
          <a href="#demo">Demo</a>
          <a href="#analysis">Analysis</a>
          <a href="#tools">Tools</a>
          <a href="#premium">Premium</a>
          <a href="#faq">FAQ</a>
        </nav>
        <InstallButton compact />
      </header>
      <main>
        <Hero />
        <Demo />
        <section className="analysis-section shell" id="analysis">
          <div className="section-heading">
            <span className="eyebrow">Full token analysis</span>
            <h2>
              One click.
              <br />
              <em>A clearer picture.</em>
            </h2>
            <p>
              Stop digging through endless token metrics. Get a structured risk
              assessment with a clear verdict and supporting evidence.
            </p>
          </div>
          <div className="verdict-grid">
            {(
              [
                ["AVOID", "Critical signals exceed configured limits.", "88"],
                ["WATCH", "Uncertainty deserves more attention.", "46"],
                ["BUY", "Available signals pass risk filters.", "18"],
              ] as const
            ).map(([n, d, s]) => (
              <article key={n} className={`verdict-card ${n.toLowerCase()}`}>
                <span>
                  {s}
                  <small>/100</small>
                </span>
                <h3>{n}</h3>
                <p>{d}</p>
              </article>
            ))}
          </div>
          <div className="checks-cloud">
            {checks.map((c, i) => (
              <span key={c}>
                <i>{String(i + 1).padStart(2, "0")}</i>
                {c}
              </span>
            ))}
          </div>
          <p className="section-disclaimer">
            Unknown data stays UNKNOWN. A passing filter is not a safety
            certification or financial advice.
          </p>
        </section>
        <section className="tools shell" id="tools">
          <div className="section-heading">
            <span className="eyebrow">Research tools</span>
            <h2>
              Evidence, without
              <br />
              <em>the tab maze.</em>
            </h2>
          </div>
          <div className="tools-grid">
            <article className="tool-card x">
              <div className="tool-card__object">𝕏</div>
              <span className="eyebrow">X Search</span>
              <h3>Follow the conversation.</h3>
              <p>
                Find public X posts mentioning a contract address. The extension
                opens the search in X.
              </p>
              <span className="round-arrow">↗</span>
            </article>
            <article className="tool-card chain">
              <div className="tool-card__object">⌬</div>
              <span className="eyebrow">Solscan Explorer</span>
              <h3>Look beyond the chart.</h3>
              <p>
                Explore holders, creator information, contract checks, and
                available on-chain evidence.
              </p>
              <span className="round-arrow">↗</span>
            </article>
          </div>
        </section>
        <section className="tracking shell">
          <div className="tracking__copy">
            <span className="eyebrow">My tracked tokens</span>
            <h2>
              Keep an eye on
              <br />
              <em>what matters.</em>
            </h2>
            <p>
              Save tokens locally and monitor price, liquidity, and risk while
              your GMGN tab and overlay are active.
            </p>
            <ul>
              <li>Risk Score changes</li>
              <li>Price and liquidity alerts</li>
              <li>Critical signals and hard blocks</li>
              <li>Cooldown and deduplication</li>
            </ul>
          </div>
          <div className="tracking-panel glass">
            <div className="tracking-panel__header">
              <b>Tracked tokens</b>
              <span className="live">● Overlay active</span>
            </div>
            {DEMO_TOKENS.map((t) => (
              <div className="tracking-card" key={t.symbol}>
                <span className="token-orb">{t.symbol[0]}</span>
                <div>
                  <b>{t.symbol}</b>
                  <small>Last check · just now</small>
                </div>
                <span>
                  Risk <b>{t.score}</b>
                </span>
                <small>{t.alert}</small>
              </div>
            ))}
            <p>Illustrative interface · Demo Data</p>
          </div>
        </section>
        <section className="premium shell" id="premium">
          <div className="premium__art">
            <i className="premium-orbit" />
            <Mascot mood="cheer" size="large" coin />
          </div>
          <div className="premium__copy">
            <span className="coming-pill">Coming soon</span>
            <h2>
              Your mascot is about
              <br />
              <em>to get smarter.</em>
            </h2>
            <p className="premium__lead">
              Meet your future personal AI memecoin trader and strategist.
            </p>
            <div className="premium-list">
              {[
                [
                  "01",
                  "Meme Launch Hunter",
                  "Monitor emerging launches against your personal chain, liquidity, age, and risk preferences.",
                ],
                [
                  "02",
                  "Personal Meme Strategist",
                  "Turn your goals into a structured research plan with entry, exit, sizing, and risk rules you control.",
                ],
                [
                  "03",
                  "Dev & Bundle Detective",
                  "Connect available developer history, bundled wallets, holder concentration, and related on-chain evidence.",
                ],
                [
                  "04",
                  "Exit & Rug Guardian",
                  "Watch verified risk and liquidity changes and explain why a token may need immediate attention.",
                ],
                [
                  "05",
                  "Ask My Mascot",
                  "Ask token questions in plain English and receive answers grounded only in available structured data.",
                ],
              ].map(([n, t, d]) => (
                <div key={n}>
                  <span>{n}</span>
                  <p>
                    <b>{t}</b>
                    <small>{d}</small>
                  </p>
                </div>
              ))}
            </div>
            <button className="premium-price" type="button" disabled>
              <span>$99</span>
              <small>/month</small>
            </button>
            <p className="premium-price__status">
              Coming Soon · Subscription not yet available
            </p>
            <small>
              No payments, automatic execution, or guaranteed signals are
              available.
            </small>
          </div>
        </section>
        <LoyaltyHolderRewards />
        <section className="faq shell" id="faq">
          <div className="section-heading">
            <span className="eyebrow">Questions, answered</span>
            <h2>
              Good research starts
              <br />
              <em>with clarity.</em>
            </h2>
          </div>
          <div className="faq-list">
            {faq.map(([q, a]) => (
              <details key={q}>
                <summary>
                  {q}
                  <span>+</span>
                </summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </section>
        <section className="final-cta shell">
          <Mascot mood="walk" />
          <span className="eyebrow">Your companion is ready</span>
          <h2>
            Research smarter.
            <br />
            <em>Stay in context.</em>
          </h2>
          <p>Bring structured Solana token research directly into GMGN.</p>
          <InstallButton />
        </section>
      </main>
      <footer>
        <div className="brand">
          <span>
            TOKIN<small>Memecoin research companion</small>
          </span>
        </div>
        <p>
          Research support, not financial advice. Crypto assets are high risk.
          Verify source data independently.
        </p>
        <nav>
          <a href="#demo">Demo</a>
          <a href="#faq">FAQ</a>
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          <a href="#home">Back to top ↑</a>
        </nav>
        <small>© 2026 TOKIN. GMGN is a third-party platform.</small>
      </footer>
      {modal && <DevFeesModal onClose={() => setModal(false)} />}
    </>
  );
}
